/**
 * appointmentUtils.js
 * Formats raw API appointment objects into the shape FullCalendar expects.
 * Eliminates the repeated .map() blocks across fetchAppointments, handleDeleteEvent,
 * handleCancelAppointmentConfirm, and loadAppointmentsByCarrier.
 */


/**
 * Extracts the dock key string from an appointment object using the same
 * priority order as the getDockKey hook in the component.
 */
export const getDockKeyFromAppt = (appt) => {
  const raw =
    appt?.dockId ??
    appt?.dock_id ??
    appt?.dockNumber ??
    appt?.dock_number ??
    appt?.resourceId ??
    appt?.dock ??
    appt?.id ??
    null;
  return raw !== null && raw !== undefined ? String(raw) : null;
};

/**
 * Returns the CSS class for an appointment based on its request type.
 */
export const getAppointmentColorClass = (appt) => {
  const type = appt.requestType?.toUpperCase();
  if (appt.shipmentStatus === "Active") return "appt-in-transit";
  if (type === "LIVE_LOAD" || !appt.isDropTrailer) return "appt-live-load";
  if (type === "DROP_TRAILER" || appt.isDropTrailer) return "appt-drop-trailer";

  return "appt-live-load";
};

/**
 * Maps a raw API appointment to a FullCalendar event object.
 *
 * @param {object} appt          - Raw appointment from the API
 * @param {object} dockNameById  - Map of dockKey → display name (from useMemo in component)
 * @param {object} options
 * @param {boolean} options.includeCarrier - Whether to prepend "Carrier: ..." to the title.
 *                                           true for Shipper view, false for Carrier view.
 */
export const formatAppointment = (appt, dockNameById, { includeCarrier = true } = {}) => {
  const salesOrderNumber = appt.salesOrderNumber;
  const dockKey = getDockKeyFromAppt(appt);
  const requestType = appt.requestType?.toUpperCase();
  const colorClass = getAppointmentColorClass(appt);
  const dockName = dockNameById[String(appt.dockNumber)] || `Dock ${appt.dockNumber}`;
  const isInTransit = appt.shipmentStatus === "Active";

  const title = includeCarrier
    ? [
        `${appt.dropOffLocation || ""}`,
        `${appt.shipmentNumber || ""}`,
        `${appt.carrierName ? appt.carrierName : ""}`,
      ].join("\n")
    : [
        `${appt.shipmentNumber || ""}`,
      ].join("\n");


  let stops = appt.shipmentStops;

  if (typeof stops === "string") {
    try {
      stops = JSON.parse(stops);
    } catch {
      stops = [];
    }
  }

  const lastStopName =
    Array.isArray(stops) && stops.length > 0
      ? stops[stops.length - 1]?.stopName
      : null;

  return {
    id: appt.appointmentId,
    title,
    start: appt.startTime,
    end: appt.endTime,
    resourceId: dockKey,
    classNames: colorClass ? colorClass.split(" ") : [],
    extendedProps: {
      carrierName:    appt.carrierName    || "-",
      shipmentNumber: appt.shipmentNumber || "-",
      shipmentStatus: appt.shipmentStatus || "-",
      stopNumber:     appt.stopNumber     || "-",
      destination:    lastStopName || "-",
      orderNumber:    salesOrderNumber    || "-",
      shipmentStops: appt.shipmentStops || [],
      dockName,
      requestType,
    },
  };
};